// Building a website from a prompt: describe it, adjust the AI's plan, then
// watch each part being designed. The result is saved as a draft and opens in
// the editor; the live site doesn't change until the owner publishes.
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ArrowDown, ArrowLeft, ArrowUp, Check, CircleAlert, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { useSitePlanMutation, useSiteSectionMutation } from "../../store/Reducer/aiApi";
import { useGetGeneralSettingsQuery } from "../../store/Reducer/user-setting";
import { EXAMPLE_PROMPTS, STYLES } from "./siteModel";

const inputClass = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

const Swatches = ({ colors }) => (
  <div className="flex gap-1.5">
    {Object.entries(colors || {}).map(([key, value]) => (
      <span key={key} title={`${key} ${value}`} className="h-7 w-7 rounded-full border border-black/10" style={{ background: value }} />
    ))}
  </div>
);

const Step = ({ n, label, active, done }) => (
  <li className={`flex items-center gap-2 text-sm ${active ? "font-semibold text-slate-900" : done ? "text-green-700" : "text-gray-400"}`}>
    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${active ? "bg-accent text-white" : done ? "bg-green-100" : "bg-gray-100"}`}>
      {done ? <Check size={13} /> : n}
    </span>
    {label}
  </li>
);

const BuildWizard = ({ initialPrompt = "", hasDraft, hasLiveSite, onBuilt, onCancel }) => {
  const [step, setStep] = useState("prompt");
  const [prompt, setPrompt] = useState(initialPrompt);
  const [style, setStyle] = useState("");
  const [colorHint, setColorHint] = useState("");
  const [language, setLanguage] = useState("en");
  // Starts in the masjid's website language (General Settings); the owner can change it.
  const { data: settings } = useGetGeneralSettingsQuery();
  useEffect(() => {
    if (String(settings?.data?.primaryLanguage || "").toLowerCase() === "arabic") setLanguage("ar");
  }, [settings]);
  const [outline, setOutline] = useState(null);
  const [parts, setParts] = useState([]); // [{ id, name, status: "waiting"|"done"|"failed", html, error }]
  const [plan, { isLoading: planning }] = useSitePlanMutation();
  const [buildSection] = useSiteSectionMutation();

  const makePlan = async () => {
    if (prompt.trim().length < 10) return toast.error("Describe your website in a sentence or two.");
    try {
      const res = await plan({ prompt: prompt.trim(), preferences: { style, colorHint, language } }).unwrap();
      setOutline(res.outline);
      setStep("outline");
    } catch (err) {
      toast.error(err?.data?.error || "The AI couldn't plan your website. Try again.");
    }
  };

  const setSections = (sections) => setOutline((o) => ({ ...o, sections }));
  const editSection = (i, patch) => setSections(outline.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const moveSection = (i, delta) => {
    const next = [...outline.sections];
    const [s] = next.splice(i, 1);
    next.splice(i + delta, 0, s);
    setSections(next);
  };
  const addSection = () => {
    const taken = new Set(outline.sections.map((s) => s.id));
    let n = outline.sections.length + 1;
    while (taken.has(`section-${n}`)) n++;
    setSections([...outline.sections, { id: `section-${n}`, name: "New section", brief: "" }]);
  };

  const buildOne = async (part) => {
    setParts((ps) => ps.map((p) => (p.id === part.id ? { ...p, status: "waiting", error: null } : p)));
    try {
      const res = await buildSection({ outline, sectionId: part.id, name: part.name, brief: part.brief }).unwrap();
      setParts((ps) => ps.map((p) => (p.id === part.id ? { ...p, status: "done", html: res.html } : p)));
      return res.html;
    } catch (err) {
      setParts((ps) => ps.map((p) => (p.id === part.id ? { ...p, status: "failed", error: err?.data?.error || "Failed" } : p)));
      return null;
    }
  };

  const build = async (only) => {
    const list = only || [
      { id: "header", name: "Header", brief: outline.header?.brief },
      ...outline.sections.filter((s) => s.name.trim()),
      { id: "footer", name: "Footer", brief: outline.footer?.brief },
    ];
    if (!only) setParts(list.map((p) => ({ ...p, status: "waiting" })));
    setStep("building");
    // All at once: the server queues them under the AI quota.
    await Promise.all(list.map(buildOne));
  };

  const done = parts.filter((p) => p.status === "done");
  const failed = parts.filter((p) => p.status === "failed");
  const waiting = parts.some((p) => p.status === "waiting");
  const usable = done.some((p) => p.id !== "header" && p.id !== "footer");

  const finish = () => {
    const byId = Object.fromEntries(done.map((p) => [p.id, p.html]));
    onBuilt({
      engine: "ai-html",
      version: 1,
      language: outline.language,
      theme: outline.theme,
      designDirection: outline.designDirection,
      seo: outline.seo,
      header: { html: byId.header || "" },
      footer: { html: byId.footer || "" },
      pages: [{
        slug: "",
        title: "Home",
        sections: outline.sections
          .filter((s) => byId[s.id])
          .map((s) => ({ id: s.id, name: s.name, html: byId[s.id], prompt: s.brief, hidden: false })),
      }],
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="flex h-14 items-center gap-3 border-b bg-white px-4">
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-lg p-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
          <ArrowLeft size={18} /> <span className="hidden sm:inline">Back</span>
        </button>
        <h1 className="flex items-center gap-2 font-semibold text-slate-800"><Sparkles size={18} className="text-violet-600" /> Build your website with AI</h1>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <ol className="mb-8 flex flex-wrap gap-x-6 gap-y-2">
          <Step n={1} label="Describe it" active={step === "prompt"} done={step !== "prompt"} />
          <Step n={2} label="Check the plan" active={step === "outline"} done={step === "building"} />
          <Step n={3} label="Build" active={step === "building"} />
        </ol>

        {step === "prompt" && (
          <section className="space-y-5">
            <div>
              <label htmlFor="site-prompt" className="mb-2 block text-lg font-semibold text-slate-800">What should your website be like?</label>
              <textarea id="site-prompt" rows={5} maxLength={2000} value={prompt} onChange={(e) => setPrompt(e.target.value)} className={inputClass}
                placeholder="e.g. A welcoming, modern website for our community masjid. Deep green and gold. Focus on our Quran classes and youth programmes." />
              <p className="mt-1 text-xs text-gray-500">Your events, news, prayer times and contact details are added automatically and stay up to date.</p>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Need an idea?</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {EXAMPLE_PROMPTS.map((p) => (
                  <button key={p} type="button" onClick={() => setPrompt(p)} className="rounded-lg border border-gray-200 bg-white p-3 text-left text-sm text-gray-700 hover:border-accent hover:bg-accent/5">{p}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Style</span>
                <select value={style} onChange={(e) => setStyle(e.target.value)} className={inputClass}>
                  <option value="">Let the AI choose</option>
                  {STYLES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Colours (optional)</span>
                <input value={colorHint} onChange={(e) => setColorHint(e.target.value)} maxLength={120} placeholder="e.g. navy and white" className={inputClass} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Language</span>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inputClass}>
                  <option value="en">English</option>
                  <option value="ar">Arabic</option>
                </select>
              </label>
            </div>
            {(hasDraft || hasLiveSite) && (
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                {hasDraft ? "Building a new website replaces your unpublished changes. " : ""}
                {hasLiveSite ? "Your current website stays live until you publish the new one." : ""}
              </p>
            )}
            <button type="button" onClick={makePlan} disabled={planning}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
              {planning ? <><Loader2 size={16} className="animate-spin" /> Planning your website…</> : <><Sparkles size={16} /> Plan my website</>}
            </button>
          </section>
        )}

        {step === "outline" && outline && (
          <section className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Look and feel</p>
                <p className="text-xs text-gray-500">{outline.theme?.fonts?.heading} and {outline.theme?.fonts?.body}. You can change colours and fonts later.</p>
              </div>
              <Swatches colors={outline.theme?.colors} />
            </div>
            <div>
              <h2 className="mb-1 text-lg font-semibold text-slate-800">Sections</h2>
              <p className="mb-3 text-sm text-gray-500">Rename, remove, reorder or describe them differently. The header and footer are added for you.</p>
              <ul className="space-y-2">
                {outline.sections.map((s, i) => (
                  <li key={s.id} className="rounded-xl border bg-white p-3">
                    <div className="flex items-center gap-2">
                      <input aria-label="Section name" value={s.name} maxLength={60} onChange={(e) => editSection(i, { name: e.target.value })}
                        className="min-w-0 flex-1 rounded-md border border-transparent px-2 py-1 font-medium text-slate-800 hover:border-gray-200 focus:border-accent focus:outline-none" />
                      <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => moveSection(i, -1)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ArrowUp size={16} /></button>
                      <button type="button" aria-label="Move down" disabled={i === outline.sections.length - 1} onClick={() => moveSection(i, 1)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ArrowDown size={16} /></button>
                      <button type="button" aria-label={`Remove ${s.name}`} onClick={() => setSections(outline.sections.filter((_, j) => j !== i))} className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>
                    </div>
                    <textarea aria-label={`What ${s.name} shows`} rows={2} maxLength={600} value={s.brief || ""} onChange={(e) => editSection(i, { brief: e.target.value })}
                      className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-600 focus:border-accent focus:outline-none" />
                  </li>
                ))}
              </ul>
              <button type="button" onClick={addSection} disabled={outline.sections.length >= 12}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:border-accent hover:text-accent disabled:opacity-40">
                <Plus size={16} /> Add a section
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => build()} disabled={!outline.sections.length}
                className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
                <Sparkles size={16} /> Build my website
              </button>
              <button type="button" onClick={() => setStep("prompt")} className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Change the description
              </button>
            </div>
          </section>
        )}

        {step === "building" && (
          <section className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{waiting ? "Designing your website…" : failed.length ? "Almost there" : "Your website is ready"}</h2>
              <p className="text-sm text-gray-500">
                {waiting ? "Each part is designed separately. This usually takes under a minute, and longer when many masjids are building at once." : failed.length ? "Some parts didn't work. Try them again, or carry on without them." : "Open it in the editor to look it over and make changes."}
              </p>
            </div>
            <ul className="divide-y rounded-xl border bg-white">
              {parts.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                  {p.status === "waiting" && <Loader2 size={16} className="animate-spin text-violet-600" />}
                  {p.status === "done" && <Check size={16} className="text-green-600" />}
                  {p.status === "failed" && <CircleAlert size={16} className="text-red-600" />}
                  <span className="flex-1 text-slate-800">{p.name}</span>
                  {p.status === "failed" && <span className="max-w-[50%] truncate text-xs text-red-600" title={p.error}>{p.error}</span>}
                </li>
              ))}
            </ul>
            {!waiting && (
              <div className="flex flex-wrap gap-3">
                {usable && (
                  <button type="button" onClick={finish} className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">
                    Open in the editor
                  </button>
                )}
                {failed.length > 0 && (
                  <button type="button" onClick={() => build(failed)} className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Try the failed parts again
                  </button>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default BuildWizard;
